import { ResourceChannel } from "@/services/resources";
import { DateTime } from "luxon";
import {
  AvailablePresets,
  ComparisonPreset,
  DateTimeISODateRangeWithPresetLabel,
  Preset,
} from "@/services/data_filters";
import * as lodash from "lodash-es";

// String key to the dataFiltersStore[key] to v-model to
export type RadioFilterStoreKeys =
  | "recommendationStatus"
  | "countryDataType"
  | "searchTermDataType"
  | "socialMediaSortByMetric"
  | "smOverviewSortByMetric"
  | "competitorMonitorSortByMetric"
  | "marketingMetricSet"
  | "waPagesSortMetric"
  | "websiteAnalyticsMetricSet"
  | "yearFilter"
  | "mpAdsSortByMetric"
  | "smMetricView"
  | "smOverviewMetricView"
  | "dashboardSortBy"
  | "aiVisibilityArea"
  | "aiVisibilityBrand"
  | "aiVisibilityProvider"
  | "smPostFormat"
  | "smCollabs";

// DATEPICKER
// TODO: Unit test presets
const startMonths = [1, 4, 7, 10];
const endMonths = [3, 6, 9, 12];

function getQuarter(
  q: number,
  y: number = DateTime.local().year,
): { start: DateTime; end: DateTime } {
  const start = DateTime.fromObject({
    year: y,
    month: startMonths[q - 1],
  }).startOf("month");
  const end = DateTime.fromObject({
    year: y,
    month: endMonths[q - 1],
  }).endOf("month");
  return { start, end };
}

function getQuartersAgo(
  q: number,
  referenceDay: DateTime,
): {
  range: { start: DateTime; end: DateTime };
  label: string;
} {
  const start = referenceDay.minus({ quarters: q }).startOf("quarter");
  const end = start.endOf("quarter");
  return {
    range: {
      start: start,
      end: modifyEndDateIfCurrentQuarter(end.quarter, end, referenceDay),
    },
    label: `Q${start.quarter} - ${start.year}`,
  };
}

function isDayInQuarter(q: number, referenceDay: DateTime): boolean {
  return (
    getQuarter(q).start <= referenceDay && referenceDay <= getQuarter(q).end
  );
}

function modifyEndDateIfCurrentQuarter(
  q: number,
  end: DateTime,
  referenceDay: DateTime,
): DateTime {
  return isDayInQuarter(q, referenceDay) ? referenceDay : end;
}
export function getMonthsAgo(
  monthsAgo: number,
  referenceDay: DateTime,
): DateTime {
  return referenceDay.minus({ months: monthsAgo }).startOf("month");
}

// Tucks the year on the month name if the month is in a different year than the reference day
export function getMonthName(dt: DateTime, referenceDay: DateTime): string {
  if (referenceDay.year !== dt.year) {
    return lodash.capitalize(dt.monthLong ?? "") + " " + dt.year;
  } else return lodash.capitalize(dt.monthLong ?? "");
}

function getSamePeriodAYearAgoComparisonPreset({
  referenceDay,
  dd,
  mm,
  startOf,
  endOf,
}: {
  referenceDay: DateTime;
  dd?: number;
  mm?: number;
  startOf?: "month" | "day";
  endOf?: "month" | "day";
}): ComparisonPreset {
  return {
    label: "Same period a year ago",
    range: () => {
      const lastYear = referenceDay.minus({ years: 1 });
      const end = lastYear.endOf(endOf ?? "day");
      const start = end
        .minus({ months: mm ?? 0, days: dd ?? 0 })
        .startOf(startOf ?? "day");
      return { start, end };
    },
  };
}
function getSamePeriodAYearAgoRange(
  referenceDay: DateTime,
  deductableDays = 0,
): { start: DateTime; end: DateTime } {
  const end = referenceDay.minus({ years: 1 }).endOf("day");
  const start = end.minus({ days: deductableDays }).startOf("day");
  return { start, end };
}
function getPreviousNDaysAgoComparisonPreset(
  n: number,
  referenceDay: DateTime,
): ComparisonPreset {
  return {
    label: `Previous ${n} days`,
    range: () => {
      const end = referenceDay.minus({ days: n }).endOf("day");
      const start = end.minus({ days: n - 1 }).startOf("day");
      return { start, end };
    },
  };
}

function getPreviousNDaysAgoRange(
  n: number,
  referenceDay: DateTime,
): { start: DateTime; end: DateTime } {
  const end = referenceDay.minus({ days: n }).endOf("day");
  const start = end.minus({ days: n - 1 }).startOf("day");
  return { start, end };
}
export function DateRangePresets(options?: {
  allowFutureDates?: boolean;
  presetMode?: "past" | "future" | "both";
}): AvailablePresets {
  const mode = options?.presetMode ?? "past";
  if (mode === "future") return futureDateRangePresets();
  if (mode === "both") {
    // past and future both define `thisMonth` / `thisYear`. Spread future
    // first so the past variants win those keys (month-/year-to-date is the
    // expected "this …" semantics), while future's forward-only keys
    // (nextMonth, next30days, …) still survive the merge. A plain
    // `{ ...past, ...future }` would instead drop the past variants.
    return { ...futureDateRangePresets(), ...pastDateRangePresets(options) };
  }
  return pastDateRangePresets(options);
}

function pastDateRangePresets(options?: {
  allowFutureDates?: boolean;
}): AvailablePresets {
  const now = DateTime.local();
  const yesterday = now.minus({ days: 1 });
  const useFutureDates = options?.allowFutureDates === true;
  return {
    today: {
      label: () => "Today",
      range: () => {
        const start = now.startOf("day");
        const end = start.endOf("day");
        return { start, end };
      },
      comparisonPresets: {
        yesterday: {
          label: "Yesterday",
          range: () => {
            const start = yesterday.startOf("day");
            const end = start.endOf("day");
            return { start, end };
          },
        },
        sameDayLastWeek: {
          label: "Same day last week",
          range: () => {
            const start = now.minus({ days: 7 }).startOf("day");
            const end = start.endOf("day");
            return { start, end };
          },
        },
        sameDayLastMonth: {
          label: "Same day last month",
          range: () => {
            const start = now.minus({ months: 1 }).startOf("day");
            const end = start.endOf("day");
            return { start, end };
          },
        },
        sameDayAYearAgo: {
          label: "Same day a year ago",
          range: () => {
            const start = now.minus({ years: 1 }).startOf("day");
            const end = start.endOf("day");
            return { start, end };
          },
        },
      },
    },
    yesterday: {
      label: () => "Yesterday",
      range: () => {
        const start = yesterday.startOf("day");
        const end = start.endOf("day");
        return { start, end };
      },
      comparisonPresets: {
        dayBeforeYesterday: {
          label: "Day before yesterday",
          range: () => {
            const start = yesterday.minus({ days: 1 }).startOf("day");
            const end = start.endOf("day");
            return { start, end };
          },
        },
        sameDayPreviousWeek: {
          label: "Same day previous week",
          range: () => {
            const start = yesterday.minus({ days: 7 }).startOf("day");
            const end = start.endOf("day");
            return { start, end };
          },
        },
        sameDayPreviousMonth: {
          label: "Same day previous month",
          range: () => {
            const start = yesterday.minus({ months: 1 }).startOf("day");
            const end = start.endOf("day");
            return { start, end };
          },
        },
        sameDayPreviousYear: {
          label: "Same day previous year",
          range: () => {
            const start = yesterday.minus({ years: 1 }).startOf("day");
            const end = start.endOf("day");
            return { start, end };
          },
        },
      },
    },
    lastWeek: {
      label: () => "Last week",
      range: () => {
        // Get the Monday of last week (ISO week starts on Monday)
        const lastMonday = now.startOf("week").minus({ weeks: 1 });
        const lastSunday = lastMonday.endOf("week");
        return { start: lastMonday, end: lastSunday };
      },
      comparisonPresets: {
        previousWeek: {
          label: "Previous week",
          range: () => {
            const twoWeeksAgoMonday = now
              .startOf("week")
              .minus({ weeks: 2 })
              .startOf("day");
            const twoWeeksAgoSunday = twoWeeksAgoMonday.endOf("week");
            return { start: twoWeeksAgoMonday, end: twoWeeksAgoSunday };
          },
        },
        sameWeekLastYear: {
          label: "Same week last year",
          range: () => {
            const lastMonday = now.startOf("week").minus({ weeks: 1 });
            const weekNumber = lastMonday.weekNumber;
            const weekYear = lastMonday.weekYear;
            const lastWeekYear = weekYear - 1;

            // Find the Monday of the same week number in the previous year
            let lastYearMonday = DateTime.fromObject({
              weekYear: lastWeekYear,
              weekNumber: weekNumber,
              weekday: 1,
            });

            // Handle edge case: week 53 might not exist in previous year
            if (!lastYearMonday.isValid && weekNumber === 53) {
              lastYearMonday = DateTime.fromObject({
                weekYear: lastWeekYear,
                weekNumber: 52,
                weekday: 1,
              });
            }

            const lastYearSunday = lastYearMonday.endOf("week");
            return { start: lastYearMonday, end: lastYearSunday };
          },
        },
      },
    },
    last7days: {
      label: () => "Last 7 days",
      range: () => {
        const end = yesterday.endOf("day");
        const start = end.minus({ days: 6 }).startOf("day");
        return { start, end };
      },
      comparisonPresets: {
        previous7days: getPreviousNDaysAgoComparisonPreset(7, yesterday),
        samePeriodAYearAgo: getSamePeriodAYearAgoComparisonPreset({
          referenceDay: yesterday,
          dd: 6,
        }),
      },
    },
    last30days: {
      label: () => "Last 30 days",
      range: () => {
        const end = yesterday.endOf("day");
        const start = end.minus({ days: 29 }).startOf("day");
        return { start, end };
      },
      comparisonPresets: {
        previous30days: getPreviousNDaysAgoComparisonPreset(30, yesterday),
        samePeriodAYearAgo: getSamePeriodAYearAgoComparisonPreset({
          referenceDay: yesterday,
          dd: 29,
        }),
      },
    },
    last60days: {
      label: () => "Last 60 days",
      range: () => {
        const end = yesterday.endOf("day");
        const start = end.minus({ days: 59 });
        return { start, end };
      },
      comparisonPresets: {
        previous60days: getPreviousNDaysAgoComparisonPreset(60, yesterday),
        samePeriodAYearAgo: getSamePeriodAYearAgoComparisonPreset({
          referenceDay: yesterday,
          dd: 59,
        }),
      },
    },
    last90days: {
      label: () => "Last 90 days",
      range: () => {
        const end = yesterday.endOf("day");
        const start = end.minus({ days: 89 }).startOf("day");
        return { start, end };
      },
      comparisonPresets: {
        previous90days: getPreviousNDaysAgoComparisonPreset(90, yesterday),
        samePeriodAYearAgo: getSamePeriodAYearAgoComparisonPreset({
          referenceDay: yesterday,
          dd: 89,
        }),
      },
    },
    thisMonth: {
      label: () => "This month",
      range: () => {
        const end = useFutureDates ? now.endOf("month") : now.endOf("day");
        const start = now.startOf("month");
        return { start, end };
      },
      comparisonPresets: {
        previousMonth: {
          label: getMonthName(getMonthsAgo(1, now), now),
          range: () => {
            const start = getMonthsAgo(1, now);
            const end = start.endOf("month");
            return { start, end };
          },
        },
        sameMonthAYearAgo: {
          label: getMonthName(getMonthsAgo(12, now), now),
          range: () => {
            const start = getMonthsAgo(12, now);
            const end = start.endOf("month");
            return { start, end };
          },
        },
        samePeriodLastMonth: {
          label: "Same period last month",
          range: () => {
            const end = useFutureDates
              ? now.minus({ months: 1 }).endOf("month")
              : now.endOf("day").minus({ months: 1 });
            const start = end.startOf("month");
            return { start, end };
          },
        },
        samePeriodAYearAgo: getSamePeriodAYearAgoComparisonPreset({
          referenceDay: now,
          startOf: "month",
          endOf: useFutureDates ? "month" : "day",
        }),
      },
    },
    lastMonth: {
      label: () => "Last month",
      range: () => {
        const start = getMonthsAgo(1, now);
        const end = start.endOf("month");
        return { start, end };
      },
      comparisonPresets: {
        previousMonth: {
          label: getMonthName(getMonthsAgo(2, now), now),
          range: () => {
            const start = getMonthsAgo(2, now);
            const end = start.endOf("month");
            return { start, end };
          },
        },
        sameMonthAYearAgo: {
          label: getMonthName(getMonthsAgo(13, now), now),
          range: () => {
            const start = getMonthsAgo(13, now);
            const end = start.endOf("month");
            return { start, end };
          },
        },
      },
    },
    currentQuarter: {
      label: () => getQuartersAgo(0, now).label,
      range: () => {
        const q = getQuartersAgo(0, now);
        if (useFutureDates) {
          return { start: q.range.start, end: q.range.start.endOf("quarter") };
        }
        return q.range;
      },
      comparisonPresets: {
        previousQuarter: {
          label: "Previous quarter",
          range: () => {
            return {
              start: getQuartersAgo(0, now)
                .range.start.minus({ quarters: 1 })
                .startOf("quarter"),
              end: getQuartersAgo(0, now)
                .range.end.minus({ quarters: 1 })
                .endOf("quarter"),
            };
          },
        },
        previousYear: {
          label: "Same quarter last year",
          range: () => {
            return {
              start: getQuartersAgo(0, now)
                .range.start.minus({ years: 1 })
                .startOf("quarter"),
              end: getQuartersAgo(0, now)
                .range.end.minus({ years: 1 })
                .endOf("quarter"),
            };
          },
        },
        samePeriodLastQuarter: {
          label: "Same period last quarter",
          range: () => {
            return {
              start: getQuartersAgo(0, now)
                .range.start.minus({ quarters: 1 })
                .startOf("day"),
              end: useFutureDates
                ? getQuartersAgo(0, now)
                    .range.start.minus({ quarters: 1 })
                    .endOf("quarter")
                : now.minus({ quarters: 1 }).endOf("day"),
            };
          },
        },
        samePeriodLastyear: {
          label: "Same period last year",
          range: () => {
            return {
              start: getQuartersAgo(0, now)
                .range.start.minus({ years: 1 })
                .startOf("day"),
              end: useFutureDates
                ? getQuartersAgo(0, now)
                    .range.start.minus({ years: 1 })
                    .endOf("quarter")
                : now.minus({ years: 1 }).endOf("day"),
            };
          },
        },
      },
    },
    qMinus1: {
      label: () => getQuartersAgo(1, now).label,
      range: () => getQuartersAgo(1, now).range,
      comparisonPresets: {
        previousQuarter: {
          label: "Previous quarter",
          range: () => {
            return {
              start: getQuartersAgo(1, now)
                .range.start.minus({ quarters: 1 })
                .startOf("quarter"),
              end: getQuartersAgo(1, now)
                .range.end.minus({ quarters: 1 })
                .endOf("quarter"),
            };
          },
        },
        previousYear: {
          label: "Same quarter last year",
          range: () => {
            return {
              start: getQuartersAgo(1, now).range.start.minus({
                years: 1,
              }),
              end: getQuartersAgo(1, now).range.end.minus({ years: 1 }),
            };
          },
        },
      },
    },
    qMinus2: {
      label: () => getQuartersAgo(2, now).label,
      range: () => getQuartersAgo(2, now).range,
      comparisonPresets: {
        previousQuarter: {
          label: "Previous quarter",
          range: () => {
            return {
              start: getQuartersAgo(2, now).range.start.minus({
                quarters: 1,
              }),
              end: getQuartersAgo(2, now).range.end.minus({
                quarters: 1,
              }),
            };
          },
        },
        previousYear: {
          label: "Same quarter last year",
          range: () => {
            return {
              start: getQuartersAgo(2, now).range.start.minus({
                years: 1,
              }),
              end: getQuartersAgo(2, now).range.end.minus({ years: 1 }),
            };
          },
        },
      },
    },
    qMinus3: {
      label: () => getQuartersAgo(3, now).label,
      range: () => getQuartersAgo(3, now).range,
      comparisonPresets: {
        previousQuarter: {
          label: "Previous quarter",
          range: () => {
            return {
              start: getQuartersAgo(3, now).range.start.minus({
                quarters: 1,
              }),
              end: getQuartersAgo(3, now).range.end.minus({
                quarters: 1,
              }),
            };
          },
        },
        previousYear: {
          label: "Same quarter last year",
          range: () => {
            return {
              start: getQuartersAgo(3, now).range.start.minus({
                years: 1,
              }),
              end: getQuartersAgo(3, now).range.end.minus({ years: 1 }),
            };
          },
        },
      },
    },
    thisYear: {
      label: () => "This year",
      range: () => {
        const end = useFutureDates ? now.endOf("year") : now.endOf("day");
        const start = end.startOf("year");
        return { start, end };
      },
      comparisonPresets: {
        previousYear: {
          label: "Last year",
          range: () => {
            const end = now.minus({ years: 1 }).endOf("year");
            const start = end.startOf("year");
            return { start, end };
          },
        },
        samePeriodPreviousYear: {
          label: "Same period last year",
          range: () => {
            const end = useFutureDates
              ? now.minus({ years: 1 }).endOf("year")
              : now.minus({ years: 1 });
            const start = end.startOf("year");
            return { start, end };
          },
        },
      },
    },
    lastYear: {
      label: () => "Last year",
      range: () => {
        const end = now.minus({ years: 1 }).endOf("year");
        const start = end.startOf("year");
        return { start, end };
      },
      comparisonPresets: {
        previousYear: {
          label: "Previous year",
          range: () => {
            const end = now.minus({ years: 2 }).endOf("year");
            const start = end.startOf("year");
            return { start, end };
          },
        },
      },
    },
  };
}

function futureDateRangePresets(): AvailablePresets {
  const now = DateTime.local();
  const today = now.startOf("day");
  return {
    thisMonth: {
      label: () => "This month",
      range: () => ({ start: today, end: now.endOf("month") }),
      comparisonPresets: {
        sameMonthLastYear: {
          label: "Same month last year",
          range: () => {
            const start = now.minus({ years: 1 }).startOf("month");
            return { start, end: start.endOf("month") };
          },
        },
        previousMonth: {
          label: getMonthName(getMonthsAgo(1, now), now),
          range: () => {
            const start = getMonthsAgo(1, now);
            return { start, end: start.endOf("month") };
          },
        },
      },
    },
    nextMonth: {
      label: () => "Next month",
      range: () => {
        const start = now.plus({ months: 1 }).startOf("month");
        return { start, end: start.endOf("month") };
      },
      comparisonPresets: {
        thisMonth: {
          label: "This month",
          range: () => {
            const start = now.startOf("month");
            return { start, end: start.endOf("month") };
          },
        },
        sameMonthLastYear: {
          label: "Same month last year",
          range: () => {
            const start = now
              .plus({ months: 1 })
              .minus({ years: 1 })
              .startOf("month");
            return { start, end: start.endOf("month") };
          },
        },
      },
    },
    next30days: {
      label: () => "Next 30 days",
      range: () => ({
        start: today,
        end: today.plus({ days: 29 }).endOf("day"),
      }),
      comparisonPresets: {
        previous30days: {
          label: "Previous 30 days",
          range: () => {
            const end = now.minus({ days: 1 }).endOf("day");
            return { start: end.minus({ days: 29 }).startOf("day"), end };
          },
        },
        samePeriodLastYear: {
          label: "Same period last year",
          range: () => {
            const start = now.minus({ years: 1 }).startOf("day");
            return { start, end: start.plus({ days: 29 }).endOf("day") };
          },
        },
      },
    },
    next60days: {
      label: () => "Next 60 days",
      range: () => ({
        start: today,
        end: today.plus({ days: 59 }).endOf("day"),
      }),
      comparisonPresets: {
        previous60days: {
          label: "Previous 60 days",
          range: () => {
            const end = now.minus({ days: 1 }).endOf("day");
            return { start: end.minus({ days: 59 }).startOf("day"), end };
          },
        },
        samePeriodLastYear: {
          label: "Same period last year",
          range: () => {
            const start = now.minus({ years: 1 }).startOf("day");
            return { start, end: start.plus({ days: 59 }).endOf("day") };
          },
        },
      },
    },
    next90days: {
      label: () => "Next 90 days",
      range: () => ({
        start: today,
        end: today.plus({ days: 89 }).endOf("day"),
      }),
      comparisonPresets: {
        previous90days: {
          label: "Previous 90 days",
          range: () => {
            const end = now.minus({ days: 1 }).endOf("day");
            return { start: end.minus({ days: 89 }).startOf("day"), end };
          },
        },
        samePeriodLastYear: {
          label: "Same period last year",
          range: () => {
            const start = now.minus({ years: 1 }).startOf("day");
            return { start, end: start.plus({ days: 89 }).endOf("day") };
          },
        },
      },
    },
    // Rolling next-4-quarters. On Dec 31 2026 → Q1/Q2/Q3/Q4 2027.
    // On mid-Q3 → remainder of Q4 this year + Q1/Q2/Q3 next year.
    ...makeNextQuarterPresets(now),
    thisYear: {
      label: () => "This year",
      range: () => ({ start: now.startOf("year"), end: now.endOf("year") }),
      comparisonPresets: {
        lastYear: {
          label: "Last year",
          range: () => {
            const start = now.minus({ years: 1 }).startOf("year");
            return { start, end: start.endOf("year") };
          },
        },
      },
    },
    restOfYear: {
      label: () => "Rest of this year",
      range: () => ({ start: today, end: now.endOf("year") }),
      comparisonPresets: {
        samePeriodLastYear: {
          label: "Same period last year",
          range: () => {
            const start = now.minus({ years: 1 }).startOf("day");
            return { start, end: now.minus({ years: 1 }).endOf("year") };
          },
        },
      },
    },
    nextYear: {
      label: () => "Next year",
      range: () => {
        const start = now.plus({ years: 1 }).startOf("year");
        return { start, end: start.endOf("year") };
      },
      comparisonPresets: {
        thisYear: {
          label: "This year",
          range: () => {
            const start = now.startOf("year");
            return { start, end: now.endOf("year") };
          },
        },
      },
    },
  };
}

function makeNextQuarterPresets(now: DateTime): Record<string, Preset> {
  const result: Record<string, Preset> = {};
  for (let i = 1; i <= 4; i++) {
    const offset = i;
    const q = now.plus({ quarters: offset }).startOf("quarter");
    result[`qPlus${offset}`] = {
      label: () => `Q${q.quarter} - ${q.year}`,
      range: () => ({ start: q, end: q.endOf("quarter") }),
      comparisonPresets: {
        sameQuarterLastYear: {
          label: "Same quarter last year",
          range: () => {
            const prev = q.minus({ years: 1 });
            return { start: prev, end: prev.endOf("quarter") };
          },
        },
        previousQuarter: {
          label: "Previous quarter",
          range: () => {
            const prev = q.minus({ quarters: 1 });
            return { start: prev, end: prev.endOf("quarter") };
          },
        },
      },
    };
  }
  return result;
}

export function getDateRangeFromPreset(
  presetLabel: string,
  comparisonLabel?: string,
): {
  preset: { start: string; end: string; preset: string };
  comparison?: { start: string; end: string; preset: string };
} {
  let start, end;
  let comparisonStart, comparisonEnd, comparisonUpdatedLabel;
  const now = DateTime.local();
  const yesterday = now.minus({ days: 1 });

  switch (presetLabel) {
    case "Last week":
      // Get the Monday of last week (ISO week starts on Monday)
      start = now.startOf("week").minus({ weeks: 1 });
      end = start.endOf("week");

      if (comparisonLabel === "Previous week") {
        comparisonStart = now
          .startOf("week")
          .minus({ weeks: 2 })
          .startOf("day");
        comparisonEnd = comparisonStart.endOf("week");
        comparisonUpdatedLabel = comparisonLabel;
      }

      if (comparisonLabel === "Same week last year") {
        const lastMonday = now.startOf("week").minus({ weeks: 1 });
        const weekNumber = lastMonday.weekNumber;
        const weekYear = lastMonday.weekYear;
        const lastWeekYear = weekYear - 1;

        // Find the Monday of the same week number in the previous year
        let lastYearMonday = DateTime.fromObject({
          weekYear: lastWeekYear,
          weekNumber: weekNumber,
          weekday: 1,
        });

        // Handle edge case: week 53 might not exist in previous year
        if (!lastYearMonday.isValid && weekNumber === 53) {
          lastYearMonday = DateTime.fromObject({
            weekYear: lastWeekYear,
            weekNumber: 52,
            weekday: 1,
          });
        }

        comparisonStart = lastYearMonday;
        comparisonEnd = lastYearMonday.endOf("week");
        comparisonUpdatedLabel = comparisonLabel;
      }

      break;

    case "Last 7 days":
      end = yesterday.endOf("day");
      start = end.minus({ days: 6 }).startOf("day");

      if (comparisonLabel === "Previous 7 days") {
        ({ start: comparisonStart, end: comparisonEnd } =
          getPreviousNDaysAgoRange(7, yesterday));
        comparisonUpdatedLabel = comparisonLabel;
      }

      if (comparisonLabel === "Same period a year ago") {
        ({ start: comparisonStart, end: comparisonEnd } =
          getSamePeriodAYearAgoRange(yesterday, 6));
        comparisonUpdatedLabel = comparisonLabel;
      }

      break;

    case "Last 30 days":
      end = yesterday.endOf("day");
      start = end.minus({ days: 29 }).startOf("day");

      if (comparisonLabel === "Previous 30 days") {
        ({ start: comparisonStart, end: comparisonEnd } =
          getPreviousNDaysAgoRange(30, yesterday));
        comparisonUpdatedLabel = comparisonLabel;
      }

      if (comparisonLabel === "Same period a year ago") {
        ({ start: comparisonStart, end: comparisonEnd } =
          getSamePeriodAYearAgoRange(yesterday, 29));
        comparisonUpdatedLabel = comparisonLabel;
      }

      break;

    case "Last 60 days":
      end = yesterday.endOf("day");
      start = end.minus({ days: 59 }).startOf("day");

      if (comparisonLabel === "Previous 60 days") {
        ({ start: comparisonStart, end: comparisonEnd } =
          getPreviousNDaysAgoRange(60, yesterday));
        comparisonUpdatedLabel = comparisonLabel;
      }

      if (comparisonLabel === "Same period a year ago") {
        ({ start: comparisonStart, end: comparisonEnd } =
          getSamePeriodAYearAgoRange(yesterday, 59));
        comparisonUpdatedLabel = comparisonLabel;
      }

      break;

    case "Last 90 days":
      end = yesterday.endOf("day");
      start = end.minus({ days: 89 }).startOf("day");
      if (comparisonLabel === "Previous 90 days") {
        ({ start: comparisonStart, end: comparisonEnd } =
          getPreviousNDaysAgoRange(90, yesterday));
        comparisonUpdatedLabel = comparisonLabel;
      }

      if (comparisonLabel === "Same period a year ago") {
        ({ start: comparisonStart, end: comparisonEnd } =
          getSamePeriodAYearAgoRange(yesterday, 89));
        comparisonUpdatedLabel = comparisonLabel;
      }
      break;

    case "Today":
      start = now.startOf("day");
      end = start.endOf("day");
      if (comparisonLabel === "Yesterday") {
        comparisonStart = yesterday.startOf("day");
        comparisonEnd = comparisonStart.endOf("day");
        comparisonUpdatedLabel = comparisonLabel;
      }
      if (comparisonLabel === "Same day last week") {
        comparisonStart = now.minus({ days: 7 }).startOf("day");
        comparisonEnd = comparisonStart.endOf("day");
        comparisonUpdatedLabel = comparisonLabel;
      }
      if (comparisonLabel === "Same day last month") {
        comparisonStart = now.minus({ months: 1 }).startOf("day");
        comparisonEnd = comparisonStart.endOf("day");
        comparisonUpdatedLabel = comparisonLabel;
      }
      if (comparisonLabel === "Same day a year ago") {
        comparisonStart = now.minus({ years: 1 }).startOf("day");
        comparisonEnd = comparisonStart.endOf("day");
        comparisonUpdatedLabel = comparisonLabel;
      }
      break;

    case "Yesterday":
      start = yesterday.startOf("day");
      end = start.endOf("day");

      if (comparisonLabel === "Day before yesterday") {
        comparisonStart = yesterday.minus({ days: 1 }).startOf("day");
        comparisonEnd = comparisonStart.endOf("day");
        comparisonUpdatedLabel = comparisonLabel;
      }
      if (comparisonLabel === "Same day previous week") {
        comparisonStart = yesterday.minus({ days: 7 }).startOf("day");
        comparisonEnd = comparisonStart.endOf("day");
        comparisonUpdatedLabel = comparisonLabel;
      }
      if (comparisonLabel === "Same day previous month") {
        comparisonStart = yesterday.minus({ months: 1 }).startOf("day");
        comparisonEnd = comparisonStart.endOf("day");
        comparisonUpdatedLabel = comparisonLabel;
      }
      if (comparisonLabel === "Same day previous year") {
        comparisonStart = yesterday.minus({ years: 1 }).startOf("day");
        comparisonEnd = comparisonStart.endOf("day");
        comparisonUpdatedLabel = comparisonLabel;
      }

      break;

    case "This month":
      end = now.endOf("day");
      start = end.startOf("month");
      if (comparisonLabel) {
        if (comparisonLabel.includes(" ")) {
          comparisonUpdatedLabel = getMonthName(getMonthsAgo(12, now), now);
          comparisonStart = getMonthsAgo(12, now);
          comparisonEnd = comparisonStart.endOf("month");
        } else {
          comparisonUpdatedLabel = getMonthName(getMonthsAgo(1, now), now);
          comparisonStart = getMonthsAgo(1, now);
          comparisonEnd = comparisonStart.endOf("month");
        }

        if (comparisonLabel === "Same period last month") {
          comparisonUpdatedLabel = getMonthName(getMonthsAgo(1, now), now);
          comparisonStart = end.startOf("month");
          comparisonEnd = now.endOf("day").minus({ months: 1 });
        }

        if (comparisonLabel === "Same period a year ago") {
          comparisonUpdatedLabel = getMonthName(getMonthsAgo(12, now), now);
          comparisonStart = start.minus({ years: 1 });
          comparisonEnd = end.minus({ years: 1 });
        }
      }

      break;

    case "Last month":
      start = getMonthsAgo(1, now);
      end = start.endOf("month");

      if (comparisonLabel) {
        if (comparisonLabel.includes(" ")) {
          comparisonUpdatedLabel = getMonthName(getMonthsAgo(13, now), now);
          comparisonStart = getMonthsAgo(13, now);
          comparisonEnd = comparisonStart.endOf("month");
        } else {
          comparisonUpdatedLabel = getMonthName(getMonthsAgo(2, now), now);
          comparisonStart = getMonthsAgo(2, now);
          comparisonEnd = comparisonStart.endOf("month");
        }
      }

      break;

    case getQuartersAgo(0, now).label:
      start = getQuartersAgo(0, now).range.start;
      end = getQuartersAgo(0, now).range.end;

      if (comparisonLabel) {
        if (comparisonLabel === "Previous quarter") {
          comparisonStart = getQuartersAgo(0, now)
            .range.start.minus({ quarters: 1 })
            .startOf("quarter");
          comparisonEnd = getQuartersAgo(0, now)
            .range.end.minus({ quarters: 1 })
            .endOf("quarter");
        }
        if (comparisonLabel === "Same quarter last year") {
          comparisonStart = getQuartersAgo(0, now)
            .range.start.minus({ years: 1 })
            .startOf("quarter");
          comparisonEnd = getQuartersAgo(0, now)
            .range.end.minus({ quarters: 1 })
            .endOf("quarter");
        }
        if (comparisonLabel === "Same period last quarter") {
          comparisonStart = getQuartersAgo(0, now)
            .range.start.minus({ quarters: 1 })
            .startOf("day");
          comparisonEnd = now.minus({ quarters: 1 }).endOf("day");
        }
        if (comparisonLabel === "Same period last year") {
          comparisonStart = getQuartersAgo(0, now)
            .range.start.minus({ years: 1 })
            .startOf("day");
          comparisonEnd = now.minus({ years: 1 }).endOf("day");
        }
      }

      break;

    case getQuartersAgo(1, now).label:
      start = getQuartersAgo(1, now).range.start;
      end = getQuartersAgo(1, now).range.end;

      if (comparisonLabel) {
        if (comparisonLabel === "Previous quarter") {
          comparisonStart = getQuartersAgo(1, now)
            .range.start.minus({ quarters: 1 })
            .startOf("quarter");
          comparisonEnd = getQuartersAgo(1, now)
            .range.end.minus({ quarters: 1 })
            .endOf("quarter");
        }
        if (comparisonLabel === "Same quarter last year") {
          comparisonStart = getQuartersAgo(1, now).range.start.minus({
            years: 1,
          });
          comparisonEnd = getQuartersAgo(1, now).range.end.minus({ years: 1 });
        }
      }

      break;

    case getQuartersAgo(2, now).label:
      start = getQuartersAgo(2, now).range.start;
      end = getQuartersAgo(2, now).range.end;

      if (comparisonLabel) {
        if (comparisonLabel === "Previous quarter") {
          comparisonStart = getQuartersAgo(2, now).range.start.minus({
            quarters: 1,
          });
          comparisonEnd = getQuartersAgo(2, now).range.end.minus({
            quarters: 1,
          });
        }
        if (comparisonLabel === "Same quarter last year") {
          comparisonStart = getQuartersAgo(2, now).range.start.minus({
            years: 1,
          });
          comparisonEnd = getQuartersAgo(2, now).range.end.minus({ years: 1 });
        }
      }

      break;

    case getQuartersAgo(3, now).label:
      start = getQuartersAgo(3, now).range.start;
      end = getQuartersAgo(3, now).range.end;

      if (comparisonLabel) {
        if (comparisonLabel === "Previous quarter") {
          comparisonStart = getQuartersAgo(3, now).range.start.minus({
            quarters: 1,
          });
          comparisonEnd = getQuartersAgo(3, now).range.end.minus({
            quarters: 1,
          });
        }
        if (comparisonLabel === "Same quarter last year") {
          comparisonStart = getQuartersAgo(3, now).range.start.minus({
            years: 1,
          });
          comparisonEnd = getQuartersAgo(3, now).range.end.minus({ years: 1 });
        }
      }

      break;

    case "This year":
      end = now.endOf("day");
      start = end.startOf("year");

      if (comparisonLabel) {
        if (comparisonLabel === "Last year") {
          comparisonStart = end.startOf("year");
          comparisonEnd = now.minus({ years: 1 }).endOf("year");
        }
        if (comparisonLabel === "Same period last year") {
          comparisonStart = end.startOf("year");
          comparisonEnd = now.minus({ years: 1 });
        }
      }

      break;

    case "Last year":
      end = now.endOf("day");
      start = end.startOf("year");

      if (comparisonLabel) {
        if (comparisonLabel === "Last year") {
          comparisonStart = end.startOf("year");
          comparisonEnd = now.minus({ years: 1 }).endOf("year");
        }
        if (comparisonLabel === "Previous year") {
          comparisonStart = end.startOf("year");
          comparisonEnd = now.minus({ years: 2 }).endOf("year");
        }
      }

      break;

    default:
      throw new Error("Unsupported preset");
  }

  function serializeDate(date: DateTime): string {
    const value = date.toISODate();
    if (value === null) throw new Error("Invalid preset dates");
    return value;
  }
  return {
    preset: {
      end: serializeDate(end),
      preset: presetLabel,
      start: serializeDate(start),
    },
    comparison:
      comparisonUpdatedLabel &&
      comparisonStart?.isValid &&
      comparisonEnd?.isValid
        ? {
            end: serializeDate(comparisonEnd),
            preset: comparisonUpdatedLabel,
            start: serializeDate(comparisonStart),
          }
        : undefined,
  };
}

export function getExpectedEnd(
  comparison: string | undefined,
  selection: DateTimeISODateRangeWithPresetLabel | undefined,
  preset: string | undefined,
): DateTime | null {
  if (!preset || preset === "Custom" || !selection?.end) {
    return null;
  }

  const savedEnd = DateTime.fromISO(selection.end);

  const yesterday = DateTime.local().minus({ days: 1 }).endOf("day");
  const lastMonthEnd = DateTime.local().minus({ months: 1 }).endOf("month");
  const lastYearEnd = DateTime.local().minus({ years: 1 }).endOf("year");
  const currentDate = DateTime.local();

  const isRollingPreset = [
    "Last 7 days",
    "Last 30 days",
    "Last 60 days",
    "Last 90 days",
  ].includes(preset);

  let expectedEnd: DateTime | null = null;

  if (isRollingPreset) {
    expectedEnd = yesterday;
  } else if (preset === "Last week") {
    // Last week ends on the Sunday of last week
    expectedEnd = DateTime.local()
      .startOf("week")
      .minus({ weeks: 1 })
      .endOf("week");
  } else if (preset === "Last month") {
    expectedEnd = lastMonthEnd;
  } else if (preset === "Last year") {
    expectedEnd = lastYearEnd;
  } else if (preset === "This month") {
    expectedEnd = currentDate;
  } else if (preset === "This year") {
    expectedEnd = currentDate;
  } else if (preset === "Today") {
    expectedEnd = DateTime.local().endOf("day");
  } else if (preset === "Yesterday") {
    expectedEnd = yesterday;
  } else if (/^Q[1-4]/.test(preset)) {
    const [quarter, yearStr] = preset.split(" - ");
    const year = Number(yearStr);
    const q = Number(quarter.replace("Q", ""));

    expectedEnd = DateTime.local(
      year,
      ({ 1: 3, 2: 6, 3: 9, 4: 12 } as Record<number, number>)[q],
    ).endOf("month");
  }

  // Return only if it differs from savedEnd
  if (expectedEnd && !savedEnd.hasSame(expectedEnd, "day")) {
    return expectedEnd;
  }

  return null;
}

export enum AdNetwork {
  MESSENGER = "messenger",
  FB_UNKNOWN = "unknown", // TODO: Separate this from the LinkedIn unknown to avoid filter collision
  FACEBOOK = "facebook",
  AUDIENCE_NETWORK = "audience_network",
  INSTAGRAM = "instagram",
  YOUTUBE_WATCH = "YOUTUBE_WATCH",
  MIXED = "MIXED",
  YOUTUBE_SEARCH = "YOUTUBE_SEARCH",
  SEARCH_PARTNERS = "SEARCH_PARTNERS",
  CONTENT = "CONTENT",
  SEARCH = "SEARCH",
  LI_UNKNOWN = "UNKNOWN",
  OFF_SITE = "OFF_SITE",
  ON_SITE = "ON_SITE",
  SNAPCHAT = "Snapchat",
  AOL_SEARCH = "AOL Search",
  AUDIENCE = "Audience",
  BING_AND_YAHOO_SEARCH = "Bing and Yahoo! search",
  MICROSOFT_SITES_AND_SELECT_TRAFFIC = "Microsoft sites and select traffic",
}

export function getChannelNetworks(channel: ResourceChannel): AdNetwork[] {
  switch (channel) {
    case ResourceChannel.FACEBOOK_ADS:
      return [
        AdNetwork.MESSENGER,
        AdNetwork.FB_UNKNOWN,
        AdNetwork.FACEBOOK,
        AdNetwork.AUDIENCE_NETWORK,
        AdNetwork.INSTAGRAM,
      ];
    case ResourceChannel.GOOGLE_ADS:
      return [
        AdNetwork.YOUTUBE_WATCH,
        AdNetwork.MIXED,
        AdNetwork.YOUTUBE_SEARCH,
        AdNetwork.SEARCH_PARTNERS,
        AdNetwork.CONTENT,
        AdNetwork.SEARCH,
      ];
    case ResourceChannel.LINKED_IN_ADS:
      return [AdNetwork.OFF_SITE, AdNetwork.LI_UNKNOWN, AdNetwork.ON_SITE];
    case ResourceChannel.SNAPCHAT_ADS:
      return [AdNetwork.SNAPCHAT];
    case ResourceChannel.BING_ADS:
      return [
        AdNetwork.AOL_SEARCH,
        AdNetwork.AUDIENCE,
        AdNetwork.BING_AND_YAHOO_SEARCH,
        AdNetwork.MICROSOFT_SITES_AND_SELECT_TRAFFIC,
      ];
  }
  return [];
}
export function getNetworkChannel(network: AdNetwork): ResourceChannel {
  return {
    [AdNetwork.MESSENGER]: ResourceChannel.FACEBOOK_ADS,
    [AdNetwork.FB_UNKNOWN]: ResourceChannel.FACEBOOK_ADS,
    [AdNetwork.FACEBOOK]: ResourceChannel.FACEBOOK_ADS,
    [AdNetwork.AUDIENCE_NETWORK]: ResourceChannel.FACEBOOK_ADS,
    [AdNetwork.INSTAGRAM]: ResourceChannel.FACEBOOK_ADS,
    [AdNetwork.YOUTUBE_WATCH]: ResourceChannel.GOOGLE_ADS,
    [AdNetwork.MIXED]: ResourceChannel.GOOGLE_ADS,
    [AdNetwork.YOUTUBE_SEARCH]: ResourceChannel.GOOGLE_ADS,
    [AdNetwork.SEARCH_PARTNERS]: ResourceChannel.GOOGLE_ADS,
    [AdNetwork.CONTENT]: ResourceChannel.GOOGLE_ADS,
    [AdNetwork.SEARCH]: ResourceChannel.GOOGLE_ADS,
    [AdNetwork.OFF_SITE]: ResourceChannel.LINKED_IN_ADS,
    [AdNetwork.LI_UNKNOWN]: ResourceChannel.LINKED_IN_ADS,
    [AdNetwork.ON_SITE]: ResourceChannel.LINKED_IN_ADS,
    [AdNetwork.SNAPCHAT]: ResourceChannel.SNAPCHAT_ADS,
    [AdNetwork.AOL_SEARCH]: ResourceChannel.BING_ADS,
    [AdNetwork.AUDIENCE]: ResourceChannel.BING_ADS,
    [AdNetwork.BING_AND_YAHOO_SEARCH]: ResourceChannel.BING_ADS,
    [AdNetwork.MICROSOFT_SITES_AND_SELECT_TRAFFIC]: ResourceChannel.BING_ADS,
  }[network];
}
